import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { MessageCircle, Menu, X, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Courses', path: '/', hash: '#courses' },
    { name: 'About', path: '/about' },
    { name: 'Verify Certificate', path: '/verify-certificate' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Companies', path: '/companies' },
    { name: 'Contact', path: '/', hash: '#contact' }
  ];

  const handleWhatsApp = () => {
    window.open('https://wa.me/919694612895', '_blank');
  };

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    if (link.hash && link.path === '/') {
      setTimeout(() => {
        const element = document.querySelector(link.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled && isMobile
          ? 'bg-gradient-to-r from-red-600 to-orange-600 backdrop-blur-md shadow-lg shadow-red-500/20'
          : isScrolled
          ? 'bg-gray-950/95 backdrop-blur-md shadow-lg shadow-red-500/5'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-18">
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_hackademics/artifacts/0t3ay2r3_icc_logo_red_without_bg-removebg-preview.png"
              alt="Indian Cyber Club Academy"
              className="h-10 sm:h-14 md:h-16 lg:h-20 w-auto transition-transform duration-300 group-hover:scale-105 drop-shadow-[0_0_20px_rgba(239,68,68,0.7)]"
            />
            <div className="hidden md:block">
              <div className="text-lg sm:text-xl md:text-2xl lg:text-2xl font-black text-white tracking-tight drop-shadow-lg">Indian Cyber Club</div>
              <div className="text-xs sm:text-sm text-red-500 font-bold drop-shadow-md">Elite Hacking Academy</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.hash ? (
                <a
                  key={link.name}
                  href={link.hash}
                  onClick={(e) => {
                    if (location.pathname !== '/') {
                      e.preventDefault();
                      window.location.href = '/' + link.hash;
                    }
                  }}
                  className="text-gray-300 hover:text-red-500 transition-colors duration-300 font-medium relative group text-sm whitespace-nowrap"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-red-500 transition-all duration-300 group-hover:w-3/4"></span>
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`transition-colors duration-300 font-medium relative group text-sm whitespace-nowrap ${
                    isActive(link.path) ? 'text-red-500' : 'text-gray-300 hover:text-red-500'
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 h-0.5 bg-red-500 transition-all duration-300 ${
                    isActive(link.path) ? 'w-3/4' : 'w-0 group-hover:w-3/4'
                  }`}></span>
                </Link>
              )
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/30"
              onClick={handleWhatsApp}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
            <Button
              size="sm"
              className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-red-500/50"
              onClick={() => window.open('https://indiancyberclub.in/checkout/c5ba72d4-c2ba-4a0b-8b5d-a7e55ba79aed', '_blank')}
            >
              <Phone className="w-4 h-4 mr-2" />
              Book 1-on-1 Call
            </Button>
          </div>

          <button
            className="lg:hidden text-white p-2 hover:bg-gray-800 rounded-lg transition-colors touch-manipulation"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-800 animate-in slide-in-from-top">
            <nav className="flex flex-col space-y-3">
              {navLinks.map((link) => (
                link.hash ? (
                  <a
                    key={link.name}
                    href={link.hash}
                    onClick={(e) => {
                      handleNavClick(link);
                      if (location.pathname !== '/') {
                        e.preventDefault();
                        window.location.href = '/' + link.hash;
                      }
                    }}
                    className="text-gray-300 hover:text-red-500 transition-colors duration-300 px-4 py-3 rounded-lg hover:bg-gray-800 touch-manipulation active:bg-gray-700"
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => handleNavClick(link)}
                    className={`transition-colors duration-300 px-4 py-3 rounded-lg hover:bg-gray-800 touch-manipulation active:bg-gray-700 ${
                      isActive(link.path) ? 'text-red-500 bg-gray-800' : 'text-gray-300 hover:text-red-500'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="flex flex-col space-y-3 px-4 pt-4 border-t border-gray-800">
                <Button
                  className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white w-full touch-manipulation shadow-lg shadow-red-500/30"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.open('https://indiancyberclub.in/checkout/c5ba72d4-c2ba-4a0b-8b5d-a7e55ba79aed', '_blank');
                  }}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Book 1-on-1 Call
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white w-full touch-manipulation"
                  onClick={() => {
                    handleWhatsApp();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Talk to Expert
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
